import React, { useState, useEffect, useRef } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Printer, Trash } from "lucide-react";

import moment from 'moment';
import Tippy from '@tippyjs/react';
import { toast } from 'react-toastify';
import TopBar from 'core/widgets/ui/TopBar';

import { useRecoilState } from 'recoil';
import { subAtom, outletAtom } from 'core/config/atoms';

import ComponentRoomGrid from 'core/widgets/util/ComponentRoomGrid';
import ComponentBookingGrid from 'core/widgets/util/ComponentBookingGrid';
import ComponentHostelGrid from 'core/widgets/util/ComponentHostelGrid';
import ComponentBookingForm from './components/ComponentBookingForm';
import ComponentHostelBookingForm from './components/ComponentHostelBookingForm';
import ComponentBookingCheckIn from './components/ComponentBookingCheckIn';
import ComponentBookingCheckOut from './components/ComponentBookingCheckOut';
import ComponentHostelBookingPayment from './components/ComponentHostelBookingPayment';

import PanelDev from 'panels/dev/PanelDev';

import ServiceBooking from 'services/serviceBooking';
import ServiceHostel from 'services/serviceHostel';
import ServicePrinter from 'services/servicePrinter';

export default function PanelReceptionMain() {
    const [sub] = useRecoilState(subAtom);
    const [outlet] = useRecoilState(outletAtom);

    const gridRef = useRef();

    const [bookings, setBookings] = useState([]);
    const [hostelBooks, setHostelBooks] = useState([]);
    const [booking, setBooking] = useState({});
    const [HBooks, setHBooks] = useState({});

    const [tab, setTab] = useState('upcoming');
    const [bookingType, setBookingType] = useState('advance');
    const [mainTab, setMainTab] = useState('room');

    const [isBook, setIsBook] = useState(false);
    const [isHostelBook, setIsHostelBook] = useState(false);
    const [isCheckIn, setIsCheckIn] = useState(false);
    const [isCheckOut, setIsCheckOut] = useState(false);
    const [openBed, setOpenBed] = useState(false);
    const [openPayment, setOpenPayment] = useState(false);

    const bookingService = new ServiceBooking();
    const hostelService = new ServiceHostel();
    const printService = new ServicePrinter();

    const createBooking = (type) => {
        setBookingType(type);
        setIsBook(true);
    }

    const onFormClose = () => {
        setIsBook(false);
        setIsCheckIn(false);
        setIsCheckOut(false);
        loadData();
        gridRef.current.refresh();
    }

    const onHostelFormClose = () => {
        setIsHostelBook(false);
        loadData();
        gridRef.current.refresh();
    }

    const onHostelPaymentClose = () => {
        setOpenPayment(false);
    }

    const gotoCheckIn = (doc) => {
        setBooking(doc);
        setIsCheckIn(true);
    }

    const gotoCheckOut = (doc) => {
        setBooking(doc);
        setIsCheckOut(true);
    }

    const viewBooking = (book) => {
        setHBooks(book);
        setOpenBed(true);
    }

    const makePayment = () => {
        setOpenPayment(true);
    }

    const makePrint = () => {
        var body = {
            "id": HBooks._id,
            "outlet": outlet.outletid.outletid
        }
        printService.printHostelBooking(body, '').then((res) => {
            toast.success("Hostel booking has sent to print!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        });
    }

    const markClose = () => {
        var body = {
            roomFloor: HBooks.roomFloor,
            roomIndex: HBooks.roomIndex,
            roomBed: HBooks.roomBed,
            custId: HBooks.customerId[0]._id,
            bookId: HBooks._id,
        };
        hostelService.checkOutBed(body, '').then((res) => {
            setHBooks({});
            setOpenBed(false);
            loadData();
            gridRef.current.refresh();
        });
    }

    const getTitleSts = (sts) => {
        switch (sts) {
            case 'booked':
                return "Upcoming";
            case 'ongoing':
            case 'inprog':
                return "On-Going";
            default:
                return "NA";
        }
    }

    const getCssSts = (sts) => {
        switch (sts) {
            case 'booked':
                return "text-blue-600";
            case 'ongoing':
                return "text-yellow-600";
            case 'inprog':
                return "text-yellow-600";
            default:
                return "NA";
        }
    }

    const getChipColor = (sts) => {
        switch (sts) {
            case 'inprog':
                return 'bg-yellow-100 text-yellow-600';
            case 'closed':
                return 'bg-green-100 text-green-600';
            case 'none':
            default:
                return 'bg-white';
        }
    }

    const getTax = (finalTotal, tax) => {
        let taxAmount = 0;
        if (tax > 0) {
            taxAmount = finalTotal - finalTotal / (1 + tax / 100);
        }
        return taxAmount.toFixed(2);
    }

    const cancelBooking = (id) => {
        let body = {
            "bid": id,
            "outletid": outlet.outletid.id,
            "outlet": outlet.outletid.outletid,
            data: {
                status: 'canceled'
            }
        }
        bookingService.updateBooking(body, '').then((res) => {
            if (res['status']) {
                loadData();
                toast.success('Booking cancelled', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const loadData = () => {
        (async function () {
            var res = await bookingService.getBookingsByFilter({ status: { "$in": ['booked', 'ongoing'] } }, '');
            setBookings(res.data);
            if (sub.apps.includes("APP00002")) {
                let body = {
                    "filter": {
                        "status": "inprog"
                    },
                    "page": 0,
                    "size": 0,
                    "searchQuery": "",
                    "startDate": "",
                    "endDate": ""
                };
                var resHostel = await hostelService.getAllBooking(body, '');
                setHostelBooks(resHostel.data.data);
                setMainTab("hostel");
                setTab("hbook");
            }
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            {isBook && <ComponentBookingForm type={bookingType} onClose={onFormClose} />}
            {isHostelBook && <ComponentHostelBookingForm onClose={onHostelFormClose} />}
            {isCheckIn && <ComponentBookingCheckIn book={booking} onClose={onFormClose} />}
            {isCheckOut && <ComponentBookingCheckOut book={booking} onClose={onFormClose} />}
            {openPayment && <ComponentHostelBookingPayment book={HBooks} onClose={onHostelPaymentClose} />}
            {
                openBed && <div className='overflow-y-auto overflow-x-hidden z-[200] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[600px] min-h-screen absolute right-0 py-[20px]'>
                        <ScrollArea>
                            <div className='px-4'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>Booking #{HBooks.bookid}</h1>
                                    <span onClick={() => {
                                        setHBooks({});
                                        setOpenBed(false);
                                    }}><i className="las la-times text-xl text-gray-700 cursor-pointer"></i></span>
                                </div>
                                <hr className='my-2' />
                                <div className='flex'>
                                    <p className='font-sans font-medium'>Order Type:
                                        <span className={`ml-4 px-3 inline-flex text-xs leading-5 font-semibold rounded-full uppercase bg-blue-100 text-blue-800`}>
                                            Hostel Booking
                                        </span>
                                    </p>
                                    <p className='ml-8 font-sans font-medium'>Status:
                                        <span className={`ml-4 px-4 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getChipColor(HBooks.status)}`}>
                                            {HBooks.status}
                                        </span>
                                    </p>
                                </div>
                                <div className='mt-1 text-sm'>
                                    Created At: <b>{moment(HBooks.createdAt).format('DD MMM, yyyy hh:mm:ss A')}</b>
                                </div>
                                <hr className='my-2' />
                                <div className='flex mt-2 space-x-2'>
                                    {
                                        HBooks.ptmStatus === "pending" && <div className="py-2 rounded-md shadow-lg text-center bg-prime text-white font-semibold cursor-pointer flex-grow" onClick={() => { makePayment(); }}>
                                            MAKE PAYMENT
                                        </div>
                                    }
                                    <div className="py-2 rounded-md shadow-lg text-center bg-black text-white font-semibold cursor-pointer flex-grow" onClick={() => { markClose(); }}>
                                        CLOSE BOOKING
                                    </div>
                                </div>
                                <div className="py-2 mt-2 rounded-md flex items-center justify-center shadow-lg text-center bg-prime text-white font-semibold cursor-pointer" onClick={() => { makePrint(); }}>
                                    <Printer className='mr-2' size={18} /> PRINT INVOICE
                                </div>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Booking Summery
                                    </span>
                                </div>
                                <div className='mt-2 flex items-center'>
                                    <p className='text-sm font-bold text-gray-950'>Item Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-bold text-gray-950'>Item Unit</p>
                                </div>
                                <hr className='my-2' />
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Booking Start</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{moment(HBooks.checkIn).format('DD MMM, yyyy')}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Booking End</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{moment(HBooks.checkOut).format('DD MMM, yyyy')}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Floor</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.roomFloor.title}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Room</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.roomFloor.units[HBooks.roomIndex].title}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Bed</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.roomFloor.units[HBooks.roomIndex].subunit[HBooks.roomBed].title}</p>
                                </div>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-2 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Payment Summery
                                    </span>
                                </div>
                                <div className="w-full mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Subtotal</span>
                                        <span className="font-medium text-sm">₹ {HBooks.total.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Extra Charges Charges</span>
                                        <span className="font-medium text-sm">₹ {HBooks.extraCharge.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">@ Discount (₹)</span>
                                        <span className="font-medium text-sm">₹ {HBooks.discount.toFixed(2)}</span>
                                    </div>
                                    {
                                        sub.features.isTax && <div className="flex justify-between">
                                            <span className="font-medium text-sm">Tax (₹)</span>
                                            <span className="font-medium text-sm">₹ {getTax(HBooks.finaltotal, HBooks.tax)}</span>
                                        </div>
                                    }
                                    <div className="border-t-2 mt-2 py-2 flex items-center justify-between">
                                        <span className="font-medium text-lg">Total</span>
                                        <span className="font-medium text-lg">₹ {HBooks.finaltotal.toFixed(2)}</span>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-2 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Customer Summery
                                    </span>
                                </div>
                                <div className='mt-2 flex items-center'>
                                    <p className='text-sm font-bold text-gray-950'>Item Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-bold text-gray-950'>Item Unit</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.customerId[0].name}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Phone</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.customerId[0].phone}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Email</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.customerId[0].email}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Collage Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.customerId[0].extra.clgName === "" ? "NA" : HBooks.customerId[0].extra.clgName}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Document Number</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{HBooks.customerId[0].extra.docNum === "" ? "NA" : HBooks.customerId[0].extra.docNum}</p>
                                </div>
                                <div className=''>
                                    <p className='text-sm  font-medium text-gray-950'>Customer Document</p>
                                    <div className='w-full border overflow-hidden rounded relative'>
                                        <img src={HBooks.customerId[0].extra.docFile.fileurl} alt='document' className='w-full object-fill' />
                                    </div>
                                </div>
                            </div>
                        </ScrollArea >
                    </div>
                </div>
            }
            <div className='h-screen w-full'>
                <div className="w-full h-full py-3 px-[4px]">
                    <div className="grid grid-cols-12 grid-gap-2">
                        <div className="col-span-12 mt-2">
                            <div className="intro-y flex items-center justify-between h-10 border-b pb-4">
                                <h2 className="text-2xl font-medium font-serif truncate ml-2 text-ascent">
                                    Reception Terminal
                                </h2>
                                <TopBar />
                            </div>
                        </div>
                    </div>
                    <div className="col-span-12">
                        <ScrollArea className="w-full h-screen overflow-hidden">
                            <div className='w-full min-h-screen bg-gray-100 rounded-md grid grid-cols-10 gap-2'>
                                <div className='col-span-7 h-full border-r p-1'>
                                    <div className='w-full px-2 my-2 flex'>
                                        {sub.apps.includes("APP00002") && <div className={`px-4 h-8 flex items-center justify-center rounded cursor-pointer ${mainTab === 'hostel' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`} onClick={() => { setMainTab('hostel') }}>Hostel</div>}
                                        <div className={`px-4 h-8 flex items-center justify-center rounded cursor-pointer ${mainTab === 'room' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`} onClick={() => { setMainTab('room') }}>Room</div>
                                        <div className={`px-4 h-8 flex items-center justify-center rounded cursor-pointer ${mainTab === 'book' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`} onClick={() => { setMainTab('book') }}>Booking</div>
                                    </div>
                                    {
                                        (() => {
                                            switch (mainTab) {
                                                case 'room':
                                                    return <ComponentRoomGrid ref={gridRef} />
                                                case 'book':
                                                    return <ComponentBookingGrid ref={gridRef} />
                                                case 'hostel':
                                                    return <ComponentHostelGrid ref={gridRef} />
                                                default:
                                                    return (<PanelDev />);
                                            }
                                        })()
                                    }
                                </div>
                                <div className='col-span-3 h-full'>
                                    <div className='flex items-center justify-between mt-2'>
                                        <h2 className="text-xl font-medium font-serif truncate text-ascent">
                                            Bookings
                                        </h2>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger>
                                                <button className="w-full py-1 px-2 h-8 rounded-md bg-prime hover:bg-primeLight text-white mr-2 font-medium">Add Booking</button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent>
                                                <div
                                                    className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                    <div>
                                                        <ul className='flex flex-col items-start p-2'>
                                                            {
                                                                sub.apps.includes("APP00002") && <DropdownMenuItem>
                                                                    <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                        setIsHostelBook(true);
                                                                    }}>
                                                                        <div className='flex items-center text-base'>
                                                                            <p className="text-sm font-serif ml-2 font-normal">Hostel Booking</p>
                                                                        </div>
                                                                    </li>
                                                                </DropdownMenuItem>
                                                            }
                                                            <DropdownMenuItem>
                                                                <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                    createBooking('advance');
                                                                }}>
                                                                    <div className='flex items-center text-base'>
                                                                        <p className="text-sm font-serif ml-2 font-normal">Advance Booking</p>
                                                                    </div>
                                                                </li>
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem>
                                                                <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                    createBooking('spot');
                                                                }}>
                                                                    <div className='flex items-center text-base'>
                                                                        <p className="text-sm font-serif ml-2 font-normal">Spot Booking</p>
                                                                    </div>
                                                                </li>
                                                            </DropdownMenuItem>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                    <div className="w-full flex justify-between items-center">
                                        <div className='h-12 bg-gray-100 rounded'>
                                            <ul className="intro-y flex text-sm font-sans space-x-2 my-2">
                                                {sub.apps.includes("APP00002") && <li className={`px-3 h-8 rounded flex items-center justify-center ${tab === 'hbook' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { setTab('hbook') }}>Hostel Bookings</button></li>}
                                                {sub.apps.includes("APP00002") && <li className={`px-3 h-8 rounded flex items-center justify-center ${tab === 'hpayment' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { setTab('hpayment') }}>Payments</button></li>}
                                                <li className={`px-3 h-8 rounded flex items-center justify-center ${tab === 'upcoming' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { setTab('upcoming') }}>Upcoming</button></li>
                                                <li className={`px-3 h-8 rounded flex items-center justify-center ${tab === 'ongoing' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { setTab('ongoing') }}>On Going</button></li>
                                            </ul>
                                        </div>
                                    </div>
                                    {
                                        (hostelBooks.length === 0 && tab === 'hbook') && <div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                            <h1 className='text-sm font-medium text-prime'>No data found.</h1>
                                        </div>
                                    }
                                    {
                                        tab === 'hbook' && <ScrollArea className="w-full h-screen overflow-hidden">
                                            {
                                                hostelBooks.map((book) => {
                                                    return <div className="col-span-6 border border-gray-300 rounded-lg p-4 shadow-md bg-white flex items-start my-2">
                                                        <div className="flex items-center space-x-2 mr-4">
                                                            <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                                <p className='text-sm font-medium text-gray-600'>FROM</p>
                                                                <p className='text-4xl font-medium'>{moment(book.checkIn).format('DD')}</p>
                                                                <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkIn).format('ddd')}</p>
                                                            </div>
                                                            <div>-</div>
                                                            <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                                <p className='text-sm font-medium text-gray-600'>TO</p>
                                                                <p className='text-4xl font-medium'>{moment(book.checkOut).format('DD')}</p>
                                                                <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkOut).format('ddd')}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-start">
                                                            <p className=''>Booking ID:&nbsp;<span className='font-medium'>{book.bookid}</span></p>
                                                            <p className='text-sm'>Name:&nbsp;<span className='font-medium'>{book.customerId[0].name}</span></p>
                                                            <p className='text-sm'>Status:&nbsp;<span className={`font-medium ${getCssSts(book.status)}`}>{(getTitleSts(book.status))}</span></p>
                                                            <div className='flex-grow'></div>
                                                            <div className='flex space-x-2'>
                                                                <Tippy content="CHECK-IN" placement='top'>
                                                                    <button className="w-full h-8 py-1 px-3 rounded bg-blue-600 text-white text-sm" onClick={() => { viewBooking(book); }}>
                                                                        VIEW
                                                                    </button>
                                                                </Tippy>
                                                            </div>
                                                        </div>
                                                    </div>
                                                })
                                            }
                                        </ScrollArea>
                                    }
                                    {
                                        (hostelBooks.filter((item) => item.ptmStatus === "pending").length === 0 && tab === 'hpayment') && <div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                            <h1 className='text-sm font-medium text-prime'>No data found.</h1>
                                        </div>
                                    }
                                    {
                                        tab === 'hpayment' && <ScrollArea className="w-full h-screen overflow-hidden">
                                            {
                                                hostelBooks.filter((item) => item.ptmStatus === "pending").map((book) => {
                                                    return <div className="col-span-6 border border-gray-300 rounded-lg p-4 shadow-md bg-white flex items-start my-2">
                                                        <div className="flex items-center space-x-2 mr-4">
                                                            <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                                <p className='text-sm font-medium text-gray-600'>FROM</p>
                                                                <p className='text-4xl font-medium'>{moment(book.checkIn).format('DD')}</p>
                                                                <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkIn).format('ddd')}</p>
                                                            </div>
                                                            <div>-</div>
                                                            <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                                <p className='text-sm font-medium text-gray-600'>TO</p>
                                                                <p className='text-4xl font-medium'>{moment(book.checkOut).format('DD')}</p>
                                                                <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkOut).format('ddd')}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-start">
                                                            <p className=''>Booking ID:&nbsp;<span className='font-medium'>{book.bookid}</span></p>
                                                            <p className='text-sm'>Name:&nbsp;<span className='font-medium'>{book.customerId[0].name}</span></p>
                                                            <p className='text-sm'>Status:&nbsp;<span className={`font-medium ${getCssSts(book.status)}`}>{(getTitleSts(book.status))}</span></p>
                                                            <p className='text-sm'>Payment Due Date:&nbsp;<span className="font-medium text-prime">{moment(book.ptmDate).format('DD, MMM yyyy')}</span></p>
                                                            <div className='flex-grow'></div>
                                                            <div className='flex space-x-2'>
                                                                <Tippy content="CHECK-IN" placement='top'>
                                                                    <button className="w-full h-8 py-1 px-3 rounded bg-blue-600 text-white text-sm" onClick={() => { viewBooking(book); }}>
                                                                        VIEW
                                                                    </button>
                                                                </Tippy>
                                                            </div>
                                                        </div>
                                                    </div>
                                                })
                                            }
                                        </ScrollArea>
                                    }
                                    {
                                        (bookings.filter((item) => item.status === "booked").length === 0 && tab === 'upcoming') && <div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                            <h1 className='text-sm font-medium text-prime'>No data found.</h1>
                                        </div>
                                    }
                                    {
                                        tab === 'upcoming' && bookings.filter((item) => item.status === "booked").map((book) => {
                                            return <div className="col-span-6 border border-gray-300 rounded-lg p-4 shadow-md bg-white flex items-start my-2">
                                                <div className="flex items-center space-x-2 mr-4">
                                                    <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                        <p className='text-sm font-medium text-gray-600'>FROM</p>
                                                        <p className='text-4xl font-medium'>{moment(book.checkIn).format('DD')}</p>
                                                        <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkIn).format('ddd')}</p>
                                                    </div>
                                                    <div>-</div>
                                                    <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                        <p className='text-sm font-medium text-gray-600'>TO</p>
                                                        <p className='text-4xl font-medium'>{moment(book.checkOut).format('DD')}</p>
                                                        <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkOut).format('ddd')}</p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    <p className=''>Booking ID:&nbsp;<span className='font-medium'>{book.bookID}</span></p>
                                                    <p className='text-sm'>Name:&nbsp;<span className='font-medium'>{book.bookName}</span></p>
                                                    <div className=''>
                                                        <p className='text-xs text-gray-600 font-medium'>Email:&nbsp;<span>{book.bookEmail}</span></p>
                                                        <p className='text-xs text-gray-600 font-medium'>Phone:&nbsp;<span>{book.bookPhone}</span></p>
                                                    </div>
                                                    <p className='text-sm my-1'>Status:&nbsp;<span className={`font-medium ${getCssSts(book.status)}`}>{(getTitleSts(book.status))}</span></p>
                                                    <div className='flex-grow'></div>
                                                    <div className='flex space-x-2'>
                                                        <Tippy content="CHECK-IN" placement='top'>
                                                            <button className="w-full h-8 py-1 px-3 rounded bg-blue-600 text-white text-sm" onClick={() => { gotoCheckIn(book); }}>
                                                                CHECK-IN
                                                            </button>
                                                        </Tippy>
                                                        <Tippy content="Cancel Booking" placement='top'>
                                                            <button className="w-10 h-8 px-3 rounded bg-red-600 text-white text-sm" onClick={() => { cancelBooking(book._id); }}>
                                                                <Trash size={16} />
                                                            </button>
                                                        </Tippy>
                                                    </div>
                                                </div>
                                            </div>
                                        })
                                    }
                                    {
                                        (bookings.filter((item) => item.status === "ongoing").length === 0 && tab === 'ongoing') && <div className='h-[114px] p-2 rounded shadow col-span-6 border border-dashed border-prime cursor-pointer hover:shadow-md flex items-center justify-center'>
                                            <h1 className='text-sm font-medium text-prime'>No data found.</h1>
                                        </div>
                                    }
                                    {
                                        tab === 'ongoing' && bookings.filter((item) => item.status === "ongoing").map((book) => {
                                            return <div className="col-span-6 border border-gray-300 rounded-lg p-4 shadow-md bg-white flex items-start my-2">
                                                <div className="flex items-center space-x-2 mr-4">
                                                    <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                        <p className='text-sm font-medium text-gray-600'>FROM</p>
                                                        <p className='text-4xl font-medium'>{moment(book.checkIn).format('DD')}</p>
                                                        <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkIn).format('ddd')}</p>
                                                    </div>
                                                    <div>-</div>
                                                    <div className='min-w-[100px] flex flex-col items-center border bg-gray-50 border-prime p-2 rounded-md space-y-3'>
                                                        <p className='text-sm font-medium text-gray-600'>TO</p>
                                                        <p className='text-4xl font-medium'>{moment(book.checkOut).format('DD')}</p>
                                                        <p className='text-sm font-medium text-gray-600 uppercase'>{moment(book.checkOut).format('ddd')}</p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    <p className=''>Booking ID:&nbsp;<span className='font-medium'>{book.bookID}</span></p>
                                                    <p className='text-sm'>Name:&nbsp;<span className='font-medium'>{book.bookName}</span></p>
                                                    <div className=''>
                                                        <p className='text-xs text-gray-600 font-medium'>Email:&nbsp;<span>{book.bookEmail}</span></p>
                                                        <p className='text-xs text-gray-600 font-medium'>Phone:&nbsp;<span>{book.bookPhone}</span></p>
                                                    </div>
                                                    <p className='text-sm my-1'>Status:&nbsp;<span className={`font-medium ${getCssSts(book.status)}`}>{(getTitleSts(book.status))}</span></p>
                                                    <div className='flex-grow'></div>
                                                    <div className='flex space-x-2'>
                                                        <Tippy content="CHECK-OUT" placement='top'>
                                                            <button className="w-full h-8 py-1 px-3 rounded bg-red-600 text-white text-sm" onClick={() => { gotoCheckOut(book); }}>
                                                                CHECK-OUT
                                                            </button>
                                                        </Tippy>
                                                    </div>
                                                </div>
                                            </div>
                                        })
                                    }
                                </div>
                            </div>
                        </ScrollArea>
                    </div>
                </div>
            </div>
        </>
    )
}
